import {
  createContext,
  type FormEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Icon } from "../components/Icon";
import { type ClinicalRuntimeConfig, loadClinicalRuntimeConfig } from "../runtime/clinicalRuntime";
import type { WorkforceSupabaseSession } from "./supabaseClient";
import { WorkforceAccountSetup } from "./WorkforceAccountSetup";
import { WorkforceClinicBoundary } from "./WorkforceClinicBoundary";
import {
  clearWorkforceAuthUrl,
  readWorkforceAuthLink,
  type WorkforceAuthLink,
} from "./workforceAuthLink";

export interface ClinicalRuntime {
  readonly mode: "supabase";
  readonly config: ClinicalRuntimeConfig;
  readonly workforce: WorkforceSupabaseSession;
  readonly switchSite?: (siteId: string, beforeChange?: () => Promise<void>) => Promise<void>;
}

/**
 * Null outside the provider: there is no browser-local fallback runtime, so a
 * surface rendered without an authenticated workforce session is a wiring
 * error, not a demo. Tests mount `ClinicalRuntimeContext.Provider` with a stub
 * (see src/test/clinicalRuntimeFixture.tsx).
 */
export const ClinicalRuntimeContext = createContext<ClinicalRuntime | null>(null);

export function useClinicalRuntime(): ClinicalRuntime {
  const runtime = useContext(ClinicalRuntimeContext);
  if (runtime === null) {
    throw new Error("useClinicalRuntime must be used inside ClinicalRuntimeProvider");
  }
  return runtime;
}

export function ClinicalRuntimeProvider({ children }: { readonly children: ReactNode }) {
  const config = useMemo(() => {
    try {
      return loadClinicalRuntimeConfig();
    } catch {
      // Configuration errors may contain URLs or values; never echo them into the page.
      return undefined;
    }
  }, []);
  if (config === undefined)
    return (
      <main className="workforce-gate" aria-labelledby="configuration-error-title">
        <section role="alert">
          <h1 id="configuration-error-title">Clinical workspace unavailable</h1>
          <p>
            The application configuration is invalid. Contact your administrator to restore access.
          </p>
        </section>
      </main>
    );
  return <Aal2WorkforceGate config={config}>{children}</Aal2WorkforceGate>;
}

/**
 * Resolves the post-authentication gate stage from the authenticator assurance level.
 * AAL2 is always ready. An AAL1 session with no verified authenticator factor (nextLevel
 * stays "aal1") is ready when workforce MFA is optional for this environment and enters
 * TOTP enrollment when it is not. Anyone who enrolled an authenticator keeps being
 * challenged for it.
 */
export function workforceStageForAssurance(
  assurance: { readonly currentLevel: string | null; readonly nextLevel: string | null } | null,
  mfaOptional: boolean,
): "ready" | "totp" | "enroll" {
  if (assurance?.currentLevel === "aal2") return "ready";
  if (assurance?.currentLevel === "aal1" && assurance.nextLevel === "aal1") {
    return mfaOptional ? "ready" : "enroll";
  }
  return "totp";
}

function Aal2WorkforceGate({
  config,
  children,
}: {
  readonly config: ClinicalRuntimeConfig;
  readonly children: ReactNode;
}) {
  const [workforce, setWorkforce] = useState<WorkforceSupabaseSession | null>(null);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [accountSetup, setAccountSetup] = useState<{ link: WorkforceAuthLink | null } | null>(
    () => {
      const link = readWorkforceAuthLink(window.location.href);
      return link === null ? null : { link };
    },
  );
  useEffect(() => {
    if (accountSetup !== null) clearWorkforceAuthUrl();
  }, [accountSetup]);
  const [stage, setStage] = useState<"loading" | "password" | "enroll" | "totp" | "ready">(
    "loading",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<{
    readonly factorId: string;
    readonly secret: string;
    readonly qrCode: string;
  } | null>(null);

  useEffect(() => {
    let active = true;
    void import("./supabaseClient").then(({ createWorkforceSupabaseSession }) => {
      if (!active) return;
      setWorkforce(
        createWorkforceSupabaseSession({
          url: config.supabaseUrl,
          publishableKey: config.supabasePublishableKey,
          mfaOptional: config.mfaOptional === true,
        }),
      );
    });
    return () => {
      active = false;
    };
  }, [config]);

  useEffect(() => {
    if (workforce === null || accountSetup !== null) return;
    let active = true;
    const refresh = async () => {
      const session = await workforce.client.auth.getSession();
      if (!active) return;
      if (session.error !== null || session.data.session === null) {
        setSessionUserId(null);
        setEnrollment(null);
        setStage("password");
        return;
      }
      setSessionUserId(session.data.session.user.id);
      const assurance = await workforce.client.auth.mfa.getAuthenticatorAssuranceLevel();
      if (!active) return;
      setStage(workforceStageForAssurance(assurance.data, config.mfaOptional === true));
    };
    void refresh();
    const subscription = workforce.client.auth.onAuthStateChange(() => void refresh());
    return () => {
      active = false;
      subscription.data.subscription.unsubscribe();
    };
  }, [workforce, config.mfaOptional, accountSetup]);

  // Entering the enrollment stage provisions a fresh TOTP factor. Abandoned
  // unverified factors are removed first so the QR code always belongs to the
  // factor the code input will verify (a stale factor's secret is unrecoverable).
  useEffect(() => {
    if (accountSetup !== null || stage !== "enroll" || workforce === null || enrollment !== null)
      return;
    let active = true;
    const begin = async () => {
      const factors = await workforce.client.auth.mfa.listFactors();
      if (factors.error !== null) {
        if (active) setError("Authenticator enrollment could not be started.");
        return;
      }
      // listFactors buckets only VERIFIED factors under data.totp; abandoned
      // unverified enrollments appear solely in data.all. Iterating data.totp
      // here would never match, and orphans would accumulate to the GoTrue
      // max-enrolled-factors cap, locking the clinician out of enrollment.
      for (const factor of factors.data.all) {
        if (factor.factor_type === "totp" && factor.status !== "verified") {
          await workforce.client.auth.mfa.unenroll({ factorId: factor.id });
        }
      }
      const enrolled = await workforce.client.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `clinical-workspace-${Date.now()}`,
      });
      if (!active) return;
      if (enrolled.error !== null) {
        setError("Authenticator enrollment could not be started.");
        return;
      }
      setEnrollment({
        factorId: enrolled.data.id,
        secret: enrolled.data.totp.secret,
        qrCode: enrolled.data.totp.qr_code,
      });
    };
    void begin();
    return () => {
      active = false;
    };
  }, [stage, workforce, enrollment, accountSetup]);

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (workforce === null) return;
    setError(null);
    const result = await workforce.client.auth.signInWithPassword({ email, password });
    setPassword("");
    if (result.error !== null) {
      setError("Workforce sign-in was not accepted.");
      return;
    }
    const assurance = await workforce.client.auth.mfa.getAuthenticatorAssuranceLevel();
    setStage(workforceStageForAssurance(assurance.data, config.mfaOptional === true));
  };
  /** Challenges and verifies one factor with the entered code; true means AAL2 was reached. */
  const verifyFactorWithCode = async (factorId: string): Promise<boolean> => {
    if (workforce === null) return false;
    const challenge = await workforce.client.auth.mfa.challenge({ factorId });
    if (challenge.error !== null) {
      setError("Authenticator challenge could not be started.");
      return false;
    }
    const verified = await workforce.client.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code,
    });
    setCode("");
    if (verified.error !== null) {
      setError("Authenticator code was not accepted.");
      return false;
    }
    await workforce.accessToken();
    setStage("ready");
    return true;
  };
  const submitTotp = async (event: FormEvent) => {
    event.preventDefault();
    if (workforce === null) return;
    setError(null);
    const factors = await workforce.client.auth.mfa.listFactors();
    const factor = factors.data?.totp.find((candidate) => candidate.status === "verified");
    if (factors.error !== null || factor === undefined) {
      setError("A verified authenticator factor is required for this clinician account.");
      return;
    }
    await verifyFactorWithCode(factor.id);
  };
  const submitEnrollment = async (event: FormEvent) => {
    event.preventDefault();
    if (enrollment === null) return;
    setError(null);
    if (await verifyFactorWithCode(enrollment.factorId)) setEnrollment(null);
  };

  if (accountSetup !== null && workforce !== null) {
    return (
      <WorkforceAccountSetup
        workforce={workforce}
        link={accountSetup.link}
        onDone={() => {
          setStage("password");
          setAccountSetup(null);
        }}
      />
    );
  }
  if (accountSetup === null && stage === "ready" && workforce !== null && sessionUserId !== null) {
    return (
      <WorkforceClinicBoundary
        key={sessionUserId}
        config={config}
        workforce={workforce}
        userId={sessionUserId}
      >
        {children}
      </WorkforceClinicBoundary>
    );
  }
  return (
    <main className="workforce-gate" aria-labelledby="workforce-gate-title">
      <section>
        <span className="ai-mark">
          <Icon name="shield" />
        </span>
        <p className="eyebrow">Protected clinical workspace</p>
        <h1 id="workforce-gate-title">
          {stage === "totp"
            ? "Verify your authenticator"
            : stage === "enroll"
              ? "Set up your authenticator"
              : "Clinician sign-in"}
        </h1>
        <p>
          {config.mfaOptional
            ? "If you have an authenticator app set up, you will enter its code before any patient information is shown."
            : "You will confirm a code from your authenticator app before any patient information is shown."}
        </p>
        {config.mfaOptional ? (
          <p className="eyebrow" role="note">
            MFA optional for this environment
          </p>
        ) : null}
        {stage === "loading" ? <p role="status">Checking workforce session…</p> : null}
        {stage === "password" ? (
          <form onSubmit={(event) => void submitPassword(event)}>
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <button type="submit" className="primary-button full">
              Continue
            </button>
            <button
              type="button"
              className="full"
              onClick={() => {
                setError(null);
                setAccountSetup({ link: null });
              }}
            >
              Forgot password or need to finish an invitation?
            </button>
          </form>
        ) : null}
        {stage === "enroll" ? (
          enrollment === null ? (
            <p role="status">Preparing authenticator enrollment…</p>
          ) : (
            <form onSubmit={(event) => void submitEnrollment(event)}>
              <p>
                This account requires an authenticator before clinical work. Scan the QR code with
                an authenticator app, then confirm its first 6-digit code.
              </p>
              <img
                className="workforce-totp-qr"
                src={enrollment.qrCode}
                alt="Authenticator enrollment QR code"
              />
              <p className="workforce-totp-secret">
                Manual entry secret: <code>{enrollment.secret}</code>
              </p>
              <label>
                6-digit code
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </label>
              <button type="submit" className="primary-button full">
                Activate authenticator
              </button>
            </form>
          )
        ) : null}
        {stage === "totp" ? (
          <form onSubmit={(event) => void submitTotp(event)}>
            <label>
              6-digit code
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </label>
            <button type="submit" className="primary-button full">
              Verify code
            </button>
          </form>
        ) : null}
        {error ? (
          <p className="workflow-blocker" role="alert">
            {error}
          </p>
        ) : null}
      </section>
    </main>
  );
}
